import { ColumnDef } from '@applift/datagrid'
import { CmpConversion } from '../../models/Conversion'
import { ConversionFilter } from '../../models/List'
import { TestPixel } from '../../models/Pixel'
import { TextCell } from '../cellTypes/TextCell'
import { ConversionNameCell } from '../cellTypes/ConversionNameCell'
import { MeasurementPartnerCell } from '../cellTypes/MeasurementPartnerCell'
import { StatusCell } from '../cellTypes/StatusCell'
import { DateCell } from '../cellTypes/DateCell'
import { ActionsCell } from '../cellTypes/ActionsCell'

interface ColumnsMapperParams {
  conversionType: Omit<ConversionFilter, 'all'> | null
  showActionColumn: boolean
  handleTestConversion?: (conversionTestData: TestPixel | null) => void
  handleDeleteConversion: (id: number) => void
}

type ColumnsMapperReturn = {
  [key in keyof CmpConversion | 'actions']?: ColumnDef<CmpConversion, any>
}

export const getColumnsMapper = ({
  conversionType,
  showActionColumn,
  handleTestConversion,
  handleDeleteConversion,
}: ColumnsMapperParams): ColumnsMapperReturn => {
  const mapper: ColumnsMapperReturn = {
    id: {
      accessorKey: 'id',
      header: 'ID',
      cell: props => {
        return <TextCell cellData={props.renderValue() as any} />
      },
      size: 80,
      maxSize: 80,
      enableSorting: false,
    },
    name: {
      accessorKey: 'name',
      header: `${
        conversionType?.toLowerCase() === 'pixel' ? 'Pixel' : 'Postback'
      } Name`,
      cell: props => {
        return (
          <ConversionNameCell
            value={props.renderValue() as string}
            conversionType={(conversionType || 'pixel') as string}
          />
        )
      },
      minSize: 260,
      size: 400,
      maxSize: 800,
      enableSorting: false,
    },
    created: {
      accessorKey: 'created',
      header: 'Created',
      cell: props => {
        return <DateCell cellData={props.renderValue() as number} />
      },
      size: 130,
      enableSorting: false,
    },
  }
  if (conversionType?.toLowerCase() === 'pixel') {
    mapper['status'] = {
      accessorKey: 'status',
      header: 'Status',
      cell: props => {
        return (
          <StatusCell
            conversion={props.row.original}
            handleTestConversion={handleTestConversion}
          />
        )
      },
      size: 170,
      enableSorting: false,
    }
  }
  if (conversionType?.toLowerCase() === 'postback') {
    mapper['partnerName'] = {
      accessorKey: 'partnerName',
      header: 'Measurement Partner',
      cell: props => {
        return (
          <MeasurementPartnerCell
            value={props.renderValue() as string}
            logoUrl={props.row.original.logoUrl}
          />
        )
      },
      size: 180,
      enableSorting: false,
    }
  }
  if (showActionColumn) {
    mapper['actions'] = {
      id: 'actions',
      header: 'Actions',
      cell: props => (
        <ActionsCell
          id={props.row.original.id}
          onDelete={handleDeleteConversion}
        />
      ),
      size: 74,
      maxSize: 74,
      enableSorting: false,
    }
  }

  return mapper
}

const PIXEL_COLS = ['id', 'name', 'status', 'created', 'actions']

const POSTBACK_COLS = ['id', 'name', 'partnerName', 'created', 'actions']

export const getColumnsOrder = (
  conversionType: Omit<ConversionFilter, 'all'>
) => {
  switch (conversionType?.toLowerCase()) {
    case 'pixel':
      return PIXEL_COLS
    case 'postback':
      return POSTBACK_COLS
    default:
      return PIXEL_COLS
  }
}

export const getColumns = (
  params: ColumnsMapperParams
): ColumnDef<CmpConversion, any>[] => {
  if (!params.conversionType) {
    return []
  }
  const mapper = getColumnsMapper(params)
  switch (params.conversionType.toLowerCase()) {
    case 'postback':
      return (POSTBACK_COLS as (keyof CmpConversion)[])
        .map(
          (col: keyof ColumnsMapperReturn) =>
            mapper[col as keyof ColumnsMapperReturn]
        )
        .filter(col => !!col) as any
    default:
      return (PIXEL_COLS as (keyof CmpConversion)[])
        .map((col: string) => mapper[col as keyof ColumnsMapperReturn])
        .filter(col => !!col) as any
  }
}
